import { Capability, FieldKind, FoundryKind, Library } from '../domain/enums.js';
import { z } from '../zod.js';
import { IsoTimestamp, JsonValue, RowVersion, Uuid } from './common.js';
import { TypeKey } from './documents.js';
/**
 * A document type as the editor needs it (the `@` picker's type chips, Tab-cycling the stub
 * type). Stage 2b's `DocTypeV1` extends it.
 *
 * `icon` and `color` are never null in an answer (CS r1, DECISIONS 63 item 3): every seeded and
 * created type sets both, and where a `doc_type` row holds null in either column the server
 * answers the type's `key` there (1.B-1 decision 6). The shape stays required, since a field made
 * nullable would break every reader that draws a chip; the constraint that ends the fallback
 * (`NOT NULL` on both columns) is a CS migration once 1.0a's paths pass.
 */
export const DocTypeSummaryV1 = z.strictObject({
    id: Uuid,
    key: TypeKey,
    name: z.string().min(1).max(200),
    namePlural: z.string().min(1).max(200),
    icon: z.string().min(1).max(200),
    color: z.string().min(1).max(200),
    library: Library,
    capabilities: z.array(Capability),
    archived: z.boolean(),
});
/** A field's key: lower case, digits and underscores, as the seeds write them (`main_or_side`). */
export const FieldKey = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
/** The field kinds whose `config` holds `options`. */
export const OPTION_FIELD_KINDS = ['select', 'multi_select'];
/** The field kinds that read links of one link type, in a `direction`. */
export const DOC_REF_FIELD_KINDS = ['doc_ref', 'doc_refs'];
/** Every other field kind: its `config` holds only the optional keys. */
export const PLAIN_FIELD_KINDS = [
    'text',
    'long_text',
    'number',
    'boolean',
    'url',
    'real_date',
    'world_date',
    'asset',
    'dice',
];
/**
 * Every `details.issue` code the types and fields writes answer with (CS r2c; DECISIONS 138 item
 * 2): the union, in order and without duplicates, of 1.B-1's `PROP_ISSUE_CODES`,
 * `VALUE_ISSUE_CODES`, `CONFIG_ISSUE_CODES` and `WRITE_ISSUE_CODES`
 * (`packages/db/src/repositories/types/`), for 1.B-2's notices. Contracts cannot import
 * `@codex/db`, so this is a copy: 1.B-1's stage 2 asserts its four lists equal it.
 */
export const TYPES_ISSUE_CODES = [
    // PROP_ISSUE_CODES
    'unknown_field',
    'archived_field',
    'not_applicable',
    'not_a_prop',
    'temporal_field',
    'unknown_asset',
    'unknown_type',
    // VALUE_ISSUE_CODES
    'wrong_type',
    'too_long',
    'line_break',
    'not_finite',
    'not_an_option',
    'duplicate_option',
    'too_many',
    'bad_url',
    'bad_date',
    'bad_dice',
    'bad_character',
    // CONFIG_ISSUE_CODES (beyond those above)
    'bad_option',
    'unknown_key',
    'bad_direction',
    // WRITE_ISSUE_CODES (beyond those above)
    'blank',
    'builtin_type',
    'archived_type',
    'reference_target',
    'session_target',
    'bad_link_type',
    'kind_mismatch',
    'bad_rename',
    'option_in_use',
    'link_type_fixed',
    'bad_template',
    'bad_position',
];
/** Which way a document-reference field reads its link type: links out of the document, or in. */
export const FIELD_REF_DIRECTIONS = ['out', 'in'];
export const FieldRefDirection = z.enum(FIELD_REF_DIRECTIONS);
/**
 * Keys any `config` may hold, kept in their stored spelling because they live inside the column:
 * `notionName` (the Notion property a field was imported from; amendments 2.2 section 5, for Phase
 * 2's importer) and `rules_path` (where a reference type's rules field reads its value in the
 * rules record; A2.1 section 4, from 1.B-1 stage 2).
 */
const CONFIG_EXTRAS = {
    notionName: z.string().min(1).max(200).optional(),
    rules_path: z.string().min(1).max(200).optional(),
};
const fieldDefBase = {
    id: Uuid,
    /** Null for a global field. */
    docTypeId: Uuid.nullable(),
    key: FieldKey,
    label: z.string().min(1).max(200),
    section: z.string().min(1).max(200).nullable(),
    sortKey: z.string().min(1).max(200).nullable(),
    temporal: z.boolean(),
    aiVisible: z.boolean(),
    /** From `archived_at`. */
    archived: z.boolean(),
};
/**
 * A field definition (1.0b deliverable 16a), frozen with the shapes migration 0005 seeds: a union
 * on `kind`, mirroring the database's `field_def_link_type_check`. A select or multi-select holds
 * `config {options}` and no link type; a document reference holds `config {direction}` and its
 * link type; every other kind holds an empty `config`. Each `config` may also hold `notionName`
 * and `rules_path`, and nothing else. The per-option rules (length, commas, line breaks,
 * uniqueness, count) are 1.B-1's `validateConfig`, answered 422 `invalid_body`; this schema checks
 * the shape.
 */
export const FieldDefV1 = z.discriminatedUnion('kind', [
    z.strictObject({
        ...fieldDefBase,
        kind: z.enum(OPTION_FIELD_KINDS),
        config: z.strictObject({ options: z.array(z.string()), ...CONFIG_EXTRAS }),
        linkTypeId: z.null(),
    }),
    z.strictObject({
        ...fieldDefBase,
        kind: z.enum(DOC_REF_FIELD_KINDS),
        config: z.strictObject({ direction: FieldRefDirection, ...CONFIG_EXTRAS }),
        linkTypeId: Uuid,
    }),
    z.strictObject({
        ...fieldDefBase,
        kind: z.enum(PLAIN_FIELD_KINDS),
        config: z.strictObject(CONFIG_EXTRAS),
        linkTypeId: z.null(),
    }),
]);
/**
 * The exit-1a type, field, conversion and retype shapes (1.0b deliverable 18; asked by 1.B-1, its
 * writes, conversion, quarantine and retype in stage 1b). Whether a configuration suits its kind,
 * the per-option rules and every other value rule are 1.B-1's checks, answered 422 `invalid_body`
 * (DECISIONS entry 38 item 26); these schemas check the shape.
 */
const Label = z.string().min(1).max(200);
const Count = z.number().int().nonnegative();
/** A document type with its fields and how many documents it holds (`types.list`). */
export const DocTypeV1 = z.strictObject({
    ...DocTypeSummaryV1.shape,
    builtin: z.boolean(),
    sortKey: z.string().min(1).max(200).nullable(),
    foundryKind: FoundryKind.nullable(),
    defaultTemplateId: Uuid.nullable(),
    schemaRev: z.number().int().min(1),
    fields: z.array(FieldDefV1),
    documentCount: Count,
});
/** `types.create`. No key: the server derives it from the name. A create appends. */
export const TypeCreateRequestV1 = z.strictObject({
    name: Label,
    namePlural: Label.optional(),
    icon: Label,
    color: Label,
    capabilities: z.array(Capability),
    foundryKind: FoundryKind.optional(),
});
/** `types.update`: any of these; `afterId` is the type it follows (null: the first place). */
export const TypeUpdateRequestV1 = z.strictObject({
    name: Label.optional(),
    namePlural: Label.optional(),
    icon: Label.optional(),
    color: Label.optional(),
    capabilities: z.array(Capability).optional(),
    foundryKind: FoundryKind.optional(),
    defaultTemplateId: Uuid.nullable().optional(),
    afterId: Uuid.nullable().optional(),
});
/** One of `FieldDefV1`'s `config` shapes: options, a direction, or the optional keys only. */
export const FieldConfigV1 = z.union([
    z.strictObject({ options: z.array(z.string()), ...CONFIG_EXTRAS }),
    z.strictObject({ direction: FieldRefDirection, ...CONFIG_EXTRAS }),
    z.strictObject(CONFIG_EXTRAS),
]);
/** `fields.create` (`docTypeId` null: a global field). A create appends. */
export const FieldCreateRequestV1 = z.strictObject({
    docTypeId: Uuid.nullable(),
    label: Label,
    key: FieldKey.optional(),
    kind: FieldKind,
    config: FieldConfigV1,
    linkTypeId: Uuid.optional(),
    temporal: z.boolean().optional(),
    aiVisible: z.boolean().optional(),
    section: Label.optional(),
});
/** `fields.update`. Kind, `temporal` and `docTypeId` never change through it. */
export const FieldUpdateRequestV1 = z.strictObject({
    label: Label.optional(),
    key: FieldKey.optional(),
    section: Label.nullable().optional(),
    afterId: Uuid.nullable().optional(),
    aiVisible: z.boolean().optional(),
    config: FieldConfigV1.optional(),
    optionRenames: z
        .array(z.strictObject({ from: z.string(), to: z.string() }))
        .min(1)
        .max(200)
        .optional(),
});
/** `fields.convertPreview` and `fields.convert`: the kind and configuration converted to. */
export const ConvertFieldRequestV1 = z.strictObject({ kind: FieldKind, config: FieldConfigV1 });
export const CONVERSION_OUTCOMES = ['kept', 'converted', 'quarantined'];
export const ConversionOutcome = z.enum(CONVERSION_OUTCOMES);
/** One value's fate: `before` and `after` are its text forms, null where there is none. */
export const ConversionSampleV1 = z.strictObject({
    documentId: Uuid,
    outcome: ConversionOutcome,
    before: z.string().nullable(),
    after: z.string().nullable(),
});
/** A conversion's report; the preview and the commit answer the same shape. */
export const ConversionReportV1 = z.strictObject({
    fieldDefId: Uuid,
    fromKind: FieldKind,
    toKind: FieldKind,
    counts: z.strictObject({ kept: Count, converted: Count, quarantined: Count }),
    optionsAdded: z.array(z.string()),
    /** At most 50, quarantined first. */
    samples: z
        .array(ConversionSampleV1)
        .max(50)
        .refine((samples) => samples.every((s, i) => i === 0 || s.outcome !== 'quarantined' || samples[i - 1]?.outcome === 'quarantined'), { message: 'quarantined samples come first' }),
});
/** `docs.retypePreview` and `docs.retype`. */
export const RetypeRequestV1 = z.strictObject({ typeId: Uuid, baseMetaVersion: RowVersion });
export const RETYPE_FIELD_OUTCOMES = ['kept', 'mapped', 'quarantined'];
export const RetypeFieldOutcome = z.enum(RETYPE_FIELD_OUTCOMES);
const Moved = z.strictObject({ repointed: Count, detached: Count });
/**
 * A retype's report. `statesRepointed` counts the document's states re-pointed to the new type's
 * live temporal field of the same key and kind, `statesKept` those that keep their field and are
 * hidden (DECISIONS entry 40 item 4). `metaVersion` is the document's version after the commit;
 * in a preview, the version the commit would write.
 */
export const RetypeReportV1 = z.strictObject({
    fromTypeId: Uuid,
    toTypeId: Uuid,
    fields: z.array(z.strictObject({
        fieldDefId: Uuid,
        /** Null when no field of the new type took the value. */
        toFieldDefId: Uuid.nullable(),
        outcome: RetypeFieldOutcome,
    })),
    links: Moved,
    placements: Moved,
    statesRepointed: Count,
    statesKept: Count,
    capabilities: z.strictObject({ lost: z.array(Capability), gained: z.array(Capability) }),
    quarantined: Count,
    metaVersion: RowVersion,
});
/** Why a value was set aside. 1.B-1 imports this list and declares none of its own. */
export const QUARANTINE_REASONS = ['conversion', 'retype', 'unknown'];
export const QuarantineReason = z.enum(QUARANTINE_REASONS);
/**
 * A value set aside in `document.props_quarantine` (`docs.quarantine`): 1.B-1's stored entry
 * `{id, value, text, fieldKey, fieldLabel, kind, reason, fromTypeId, at}` with the field id that
 * keys it. `value` is exactly as it was stored; `text` its text form then (null for an `asset`).
 */
export const QuarantinedValueV1 = z.strictObject({
    fieldDefId: Uuid,
    id: Uuid,
    value: JsonValue,
    text: z.string().nullable(),
    fieldKey: FieldKey,
    fieldLabel: Label,
    kind: FieldKind,
    reason: QuarantineReason,
    /** Set only when a retype set the value aside. */
    fromTypeId: Uuid.nullable(),
    at: IsoTimestamp,
});
//# sourceMappingURL=types.js.map